import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PublicOffOutlinedIcon from '@mui/icons-material/PublicOffOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import RowAction from './RowAction';
import { formatDuration, formatPrice, isInactive } from '../procedureFormat';
import styles from './ProcedureTable.module.css';

const ONLY_MANAGERS = 'Apenas proprietários e administradores podem';

/** Uma linha da lista: nome e descrição, valor, duração, agendamento público e ações. */
function ProcedureRow({ procedure, canManage, reactivateLimitMessage, onEdit, onInactivate, onReactivate }) {
    const inactive = isInactive(procedure);
    // Inativo nunca aparece no agendamento público, mesmo com a marcação ligada
    const isPublic = !inactive && !!procedure.public_schedule_enabled;
    const publicTitle = inactive
        ? 'Inativo: não aparece no agendamento público'
        : (isPublic ? 'Habilitado no agendamento público' : 'Desabilitado no agendamento público');
    const publicLabel = inactive ? 'Inativo' : (isPublic ? 'Disponível' : 'Desligado');

    return (
        <tr className={inactive ? styles.ghostRow : undefined}>
            <td>
                <div className={styles.identity}>
                    <span className={styles.avatar}>
                        <MedicalServicesOutlinedIcon fontSize="small" />
                    </span>
                    <span className={styles.identityText}>
                        <span className={styles.name}>
                            <span className={styles.nameText}>{procedure.Label}</span>
                            {inactive && <span className={styles.inactiveBadge}>Inativo</span>}
                        </span>
                        {procedure.Description
                            ? <span className={styles.description}>{procedure.Description}</span>
                            : <span className={styles.emptyDescription}>Sem descrição</span>}
                    </span>
                </div>
            </td>
            <td>
                <span className={styles.price}>{formatPrice(procedure.Price)}</span>
            </td>
            <td>
                <span className={styles.duration}>
                    <AccessTimeRoundedIcon className={styles.durationIcon} />
                    {formatDuration(procedure.Duration)}
                </span>
            </td>
            <td>
                <Tooltip title={publicTitle} arrow>
                    <span className={`${styles.publicPill} ${isPublic ? styles.publicOn : styles.publicOff}`}>
                        {isPublic
                            ? <PublicOutlinedIcon className={styles.publicIcon} />
                            : <PublicOffOutlinedIcon className={styles.publicIcon} />}
                        {publicLabel}
                    </span>
                </Tooltip>
            </td>
            <td>
                <div className={styles.actions}>
                    {inactive ? (
                        <RowAction
                            title={!canManage ? `${ONLY_MANAGERS} reativar procedimentos` : (reactivateLimitMessage || 'Reativar procedimento')}
                            label={`Reativar ${procedure.Label}`}
                            onClick={() => onReactivate(procedure)}
                            tone="restore"
                            disabled={!canManage}
                        >
                            <RestoreOutlinedIcon />
                        </RowAction>
                    ) : (
                        <>
                            <RowAction
                                title={!canManage ? `${ONLY_MANAGERS} editar procedimentos` : 'Editar procedimento'}
                                label={`Editar ${procedure.Label}`}
                                onClick={() => onEdit(procedure)}
                                disabled={!canManage}
                            >
                                <EditOutlinedIcon />
                            </RowAction>
                            <RowAction
                                title={!canManage ? `${ONLY_MANAGERS} inativar procedimentos` : 'Inativar procedimento'}
                                label={`Inativar ${procedure.Label}`}
                                onClick={() => onInactivate(procedure)}
                                tone="danger"
                                disabled={!canManage}
                            >
                                <PowerSettingsNewOutlinedIcon />
                            </RowAction>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

export default ProcedureRow;
